import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useDemoSafeMutation } from "@/hooks/useDemoGuard";
import { supabase } from "@/integrations/supabase/client";
import type { RegaDisplayData } from "@/types/rega";
import { listingsRepository } from "@/repositories/listings";
import { unitsRepository } from "@/repositories/units";
import { useAuth } from "./useAuth";
import { useLanguage } from "@/contexts/LanguageContext";
import { serializeError } from "@/lib/sentry";
import { NestRequestError } from "@/services/nestRequest";
import { isListingActive, isListingExpired } from "@/lib/listingStatus";
import { toast } from "sonner";
import { LISTING_AD_LICENSE_REQUIRED_CODE } from "@dallali/shared";

/**
 * True when a listing mutation failed because RLS / DB privileges blocked it.
 * An UPDATE blocked by the policy's USING clause matches zero rows, which the
 * mutationFn surfaces as "not found or access denied"; an INSERT/WITH CHECK
 * failure surfaces as Postgres code 42501. Both mean "you lack the
 * advertisements permission", so we show actionable guidance instead of a
 * blank "an error occurred".
 *
 * RETIRE-WHEN (Great Migration N-2): the `42501` arm is PostgREST-specific.
 * Drop it in the SAME PR that repoints this hook's last direct Supabase write
 * to Nest (`tools/ci/authz-split-baseline.json` for src/hooks/useAds.ts == 0);
 * the message-pattern arm covers the Nest 403 shape and stays.
 */
export function isListingPermissionError(error: unknown): boolean {
  const code = (error as { code?: string } | null)?.code;
  const message = error instanceof Error ? error.message : String(error ?? "");
  return (
    code === "42501" ||
    /permission denied|row-level security|not found or access denied/i.test(message)
  );
}

/** True when the API refused to put a listing live without a REGA ad licence. */
export function isAdLicenseRequiredError(error: unknown): boolean {
  return error instanceof NestRequestError && error.code === LISTING_AD_LICENSE_REQUIRED_CODE;
}

export interface Listing {
  id: string;
  company_id: string;
  property_id: string;
  unit_id: string;
  title: string;
  title_en?: string;
  description?: string;
  listing_type: "rent" | "sale";
  price?: number;
  is_active: boolean;
  is_featured: boolean;
  is_negotiable: boolean;
  views_count: number;
  inquiries_count: number;
  published_at?: string;
  expires_at?: string;
  created_at: string;
  updated_at: string;
  rega_display_data?: RegaDisplayData | null;
  ad_license_number?: string | null;
}

export function useListings() {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["listings", companyId],
    queryFn: () => (companyId ? listingsRepository.listForCompany(companyId) : []),
    enabled: !!companyId,
  });
}

// Hook to get listings for a specific unit
export function useListingsByUnit(unitId: string | undefined) {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["listings-by-unit", unitId, companyId],
    queryFn: () =>
      unitId && companyId ? listingsRepository.listByUnit(unitId, companyId) : [],
    enabled: !!unitId && !!companyId,
  });
}

// Hook to get a single listing with full details
export function useListing(id: string | undefined) {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["listing", id, companyId],
    queryFn: () =>
      id && companyId ? listingsRepository.getByIdForCompany(id, companyId) : null,
    enabled: !!id && !!companyId,
  });
}

// Hook to get available (vacant) units not currently advertised
export function useAvailableUnitsForAd() {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["available-units-for-ad", companyId],
    queryFn: async () => {
      if (!companyId) return [];

      // Active listing unit IDs come from the listings repository (centralized).
      const activeUnitIds = await listingsRepository.listActiveUnitIds(companyId);

      // Get available units not in active listings (units slice — lives in
      // unitsRepository, not the listings repository).
      return unitsRepository.listAvailableForAd(companyId, activeUnitIds);
    },
    enabled: !!companyId,
  });
}

// Helper function to upload images to Supabase Storage. Listings are
// PUBLIC-imagery (see PUBLIC_MEDIA_FOLDERS in src/lib/storage.ts), so this
// stays on the `media` bucket — but #3059 C1 phase 2's
// `media_insert_company_scoped_public_folders` policy now requires the
// company id as the FIRST path segment (`{companyId}/{folder}/…`), not the
// last, so the object lands under the caller's own company scope.
async function uploadListingImages(
  files: File[],
  companyId: string
): Promise<string[]> {
  const uploadedUrls: string[] = [];

  for (const file of files) {
    const fileExt = file.name.split('.').pop();
    const filePath = `${companyId}/listings/${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from('media')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (uploadError) {
      continue;
    }

    const { data: urlData } = supabase.storage
      .from('media')
      .getPublicUrl(filePath);

    if (urlData?.publicUrl) {
      uploadedUrls.push(urlData.publicUrl);
    }
  }

  return uploadedUrls;
}

// Mutation to create a new listing
export function useCreateListing() {
  const { companyId } = useAuth();
  const queryClient = useQueryClient();
  const { t } = useLanguage();

  return useDemoSafeMutation({
    mutationFn: async (data: {
      unit_id: string;
      property_id: string;
      title: string;
      description?: string;
      listing_type: "rent" | "sale";
      price?: number;
      published_at?: string;
      expires_at?: string;
      images?: File[];
      existingImages?: string[];
      rega_display_data?: RegaDisplayData | null;
      ad_license_number?: string | null;
    }) => {
      if (!companyId) throw new Error("No company ID");

      // Upload new images
      let imageUrls: string[] = data.existingImages || [];
      if (data.images && data.images.length > 0) {
        const uploadedUrls = await uploadListingImages(data.images, companyId);
        imageUrls = [...imageUrls, ...uploadedUrls];
      }

      // Remove images and existingImages from data before insert
      const { images: _images, existingImages: _existingImages, ...listingData } = data;

      const created = await listingsRepository.create({
        company_id: companyId,
        ...listingData,
        image_urls: imageUrls,
        is_active: true,
      });

      // Fire-and-forget AR↔EN auto-translation. Soft-fails inside the
      // repository — listing has already saved either way. We *do* await
      // so the listings list refetch in onSuccess sees the translated
      // copies on the very first render; ~0.5–1.5s of extra wait per save.
      if (created && typeof (created as { id?: unknown }).id === "string") {
        await listingsRepository.translate((created as { id: string }).id);
      }

      return created;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["listings"] });
      queryClient.invalidateQueries({ queryKey: ["listings-stats"] });
      queryClient.invalidateQueries({ queryKey: ["available-units-for-ad"] });
      queryClient.invalidateQueries({ queryKey: ["public-listings"] });
      toast.success("تم نشر الإعلان بنجاح");
    },
    onError: (error) => {
      console.error("Create listing error:", serializeError(error));
      if (isListingPermissionError(error)) {
        toast.error(
          t(
            'ليس لديك صلاحية لنشر الإعلانات. اطلب من المسؤول تفعيل صلاحية "الإعلانات" لدورك.',
            'You don\'t have permission to publish ads. Ask an admin to enable the "Advertisements" permission for your role.'
          )
        );
        return;
      }
      if (isAdLicenseRequiredError(error)) {
        toast.error(t(
          "لا يمكن تفعيل الإعلان بدون رقم ترخيص إعلان من الهيئة العامة للعقار. أضف الترخيص أولاً.",
          "This ad can't go live without a REGA ad licence number. Add the licence first.",
        ));
        return;
      }
      const detail = error instanceof Error ? error.message : "";
      toast.error(
        detail
          ? t(`تعذّر نشر الإعلان: ${detail}`, `Couldn't publish the ad: ${detail}`)
          : t("حدث خطأ أثناء نشر الإعلان", "An error occurred while publishing the ad")
      );
    },
  });
}

// Mutation to update a listing
export function useUpdateListing() {
  const queryClient = useQueryClient();
  const { companyId } = useAuth();
  const { t } = useLanguage();

  return useDemoSafeMutation({
    mutationFn: async ({ id, newImages, ...data }: {
      id: string;
      title?: string;
      description?: string;
      listing_type?: "rent" | "sale";
      price?: number;
      is_active?: boolean;
      published_at?: string;
      expires_at?: string;
      image_urls?: string[];
      newImages?: File[];
    }) => {
      if (!companyId) throw new Error("No company ID");

      // Upload new images if provided
      if (newImages && newImages.length > 0) {
        const uploadedUrls = await uploadListingImages(newImages, companyId);
        data.image_urls = [...(data.image_urls || []), ...uploadedUrls];
      }

      const updated = await listingsRepository.update(id, companyId, data);

      // Re-translate ONLY when text actually changed in this edit. Price /
      // is_active / dates edits don't need to pay the Claude round-trip.
      const textChanged =
        typeof data.title === "string" || typeof data.description === "string";
      if (textChanged) {
        await listingsRepository.translate(id);
      }

      return updated;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["listings"] });
      queryClient.invalidateQueries({ queryKey: ["listings-stats"] });
      queryClient.invalidateQueries({ queryKey: ["listing"] });
      queryClient.invalidateQueries({ queryKey: ["available-units-for-ad"] });
      toast.success("تم تحديث الإعلان بنجاح");
    },
    onError: (error) => {
      console.error("Update listing error:", serializeError(error));
      if (isListingPermissionError(error)) {
        toast.error(
          t(
            'ليس لديك صلاحية لتعديل الإعلانات. اطلب من المسؤول تفعيل صلاحية "الإعلانات" لدورك.',
            'You don\'t have permission to modify ads. Ask an admin to enable the "Advertisements" permission for your role.'
          )
        );
        return;
      }
      if (isAdLicenseRequiredError(error)) {
        toast.error(t(
          "لا يمكن تفعيل الإعلان بدون رقم ترخيص إعلان من الهيئة العامة للعقار. أضف الترخيص أولاً.",
          "This ad can't go live without a REGA ad licence number. Add the licence first.",
        ));
        return;
      }
      const detail = error instanceof Error ? error.message : "";
      toast.error(
        detail
          ? t(`تعذّر تحديث الإعلان: ${detail}`, `Couldn't update the ad: ${detail}`)
          : t("حدث خطأ أثناء تحديث الإعلان", "An error occurred while updating the ad")
      );
    },
  });
}

export function useListingsStats(range?: { startDate?: string; endDate?: string }) {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["listings-stats", companyId, range?.startDate, range?.endDate],
    queryFn: async () => {
      if (!companyId) return { total: 0, active: 0, expired: 0 };

      const data = await listingsRepository.listStats(companyId, range);

      const now = new Date();
      const total = data?.length || 0;
      const active = data?.filter(l => isListingActive(l, now)).length || 0;
      const expired = data?.filter(l => isListingExpired(l, now)).length || 0;

      return { total, active, expired };
    },
    enabled: !!companyId,
  });
}
